import React from 'react';
import { useTranslation } from 'react-i18next';
import { Card, TextField, TextareaField, Button } from '@dopomogai/ui';
import { CodeEditorField } from '@dopomogai/ui';
import { Sparkles, Edit } from 'lucide-react';
import { SlideData, PresentationTheme } from '@dopomogai/types';

interface SlideEditorProps {
  slide: SlideData | null;
  theme: PresentationTheme;
  onUpdate: (id: string, updates: Partial<SlideData>) => void;
  onRefactor: (id: string, content: string) => Promise<string>;
}

export const SlideEditor: React.FC<SlideEditorProps> = ({ slide, theme, onUpdate, onRefactor }) => {
  const { t } = useTranslation(['slide_editor', 'common']);
  const [isRefactoring, setIsRefactoring] = React.useState(false);

  if (!slide) {
    return (
      <Card className="h-full">
        <div className="flex flex-col items-center justify-center h-full text-center text-slate-500">
        <div className="w-20 h-20 bg-slate-100 rounded-full flex items-center justify-center mb-4">
            <Edit size={32} className="text-slate-400" /> {/* Changed from Sparkles to Edit */}
        </div>
          <h3 className="text-xl font-semibold text-slate-700">{t('select_slide_title', 'Select a Slide')}</h3>
          <p className="max-w-xs">{t('select_slide_desc', 'Choose a slide from the left panel to begin editing its content and notes.')}</p>
        </div>
      </Card>
    );
  }
  
  const handleRefactorClick = async () => {
    setIsRefactoring(true);
    try {
      const refactoredContent = await onRefactor(slide.id, slide.content);
      // Immediately update the parent state with the refactored content
      onUpdate(slide.id, { content: refactoredContent });
    } catch (error) {
      console.error("Refactoring failed:", error);
      alert("AI Refactoring failed. See console for details.");
    } finally {
      setIsRefactoring(false);
    }
  };

  return (
    <Card
      title={t('card_title', 'Edit Slide')}
      icon={<Edit size={20} />}
      className="h-full flex flex-col"
      noPadding
    >
      <div className="p-6 flex-grow flex flex-col gap-4">
        <TextField
          label={t('slide_title_label', 'Slide Title')}
          value={slide.title}
          onChange={(e) => onUpdate(slide.id, { title: e.target.value })}
        />

        <div className="flex-grow flex flex-col">
           <CodeEditorField
              label={t('slide_content_label', 'Slide Content (JSX)')}
              value={slide.content}
              onChange={(value) => onUpdate(slide.id, { content: value || '' })}
              language="javascript"
              height="100%"
              minHeight="200px"
           />
        </div>

        <TextareaField
          label={t('speaker_notes_label', 'Speaker Notes')}
          value={slide.notes || ''}
          onChange={(e) => onUpdate(slide.id, { notes: e.target.value })}
          rows={4}
        />
      </div>

      <div className="p-4 border-t-2 border-black bg-slate-50 flex justify-end items-center">
        <Button
            variant="ghost"
            onClick={handleRefactorClick}
            disabled={isRefactoring}
            icon={<Sparkles size={16} />}
            text={isRefactoring ? t('common:refactoring', 'Refactoring...') : t('refactor_with_ai', 'Refactor with AI')}
            className="text-purple-600 hover:bg-purple-100"
        />
      </div>
    </Card>
  );
};